import { useEffect, useRef } from "react";
import { useTheme } from "@app/ui";
import { useLocalDayKey } from "./useLocalDayKey.js";

/* ── Day-change tick ───────────────────────────────────────────────── */

/** Returns a key that changes whenever the local calendar day rolls over.
 *  Schedules a precise timer to the next local midnight (not a coarse poll),
 *  and also re-checks on tab refocus so a device that slept across midnight
 *  catches up the moment it wakes. Setting state to the same value is a no-op,
 *  so refocus within the same day causes no re-render. */
/* ── Shared canvas drawing ─────────────────────────────────────────── */

/** Draw the calendar-day icon onto any canvas 2D context at the given size. */
function drawCalendarIcon(ctx: CanvasRenderingContext2D, size: number, resolvedTheme: "light" | "dark") {
  const day = new Date().getDate();

  ctx.clearRect(0, 0, size, size);

  const pad = Math.round(size * 0.047);   // ~12 at 256
  const x = pad;
  const y = pad;
  const w = size - pad * 2;
  const h = size - pad * 2;
  const r = Math.round(size * 0.1875);    // ~48 at 256

  // 1. Card background with subtle shadow
  ctx.save();
  ctx.shadowColor = resolvedTheme === "dark" ? "rgba(0, 0, 0, 0.6)" : "rgba(0, 0, 0, 0.15)";
  ctx.shadowBlur = Math.round(size * 0.047);
  ctx.shadowOffsetY = Math.round(size * 0.023);

  ctx.beginPath();
  if (ctx.roundRect) {
    ctx.roundRect(x, y, w, h, r);
  } else {
    drawRoundRectFallback(ctx, x, y, w, h, r);
  }
  ctx.closePath();

  // Diagonal gradient: turquoise-blue → warm yellow/orange
  const bgGrad = ctx.createLinearGradient(x, y + h, x + w, y);
  if (resolvedTheme === "dark") {
    bgGrad.addColorStop(0, "#0d4f5a");
    bgGrad.addColorStop(1, "#5a3d0d");
  } else {
    bgGrad.addColorStop(0, "#b2ebf2");
    bgGrad.addColorStop(1, "#ffe0b2");
  }
  ctx.fillStyle = bgGrad;
  ctx.fill();
  ctx.restore();

  // 2. Border
  ctx.save();
  ctx.beginPath();
  if (ctx.roundRect) {
    ctx.roundRect(x, y, w, h, r);
  } else {
    drawRoundRectFallback(ctx, x, y, w, h, r);
  }
  ctx.closePath();
  ctx.strokeStyle = resolvedTheme === "dark" ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.08)";
  ctx.lineWidth = Math.max(1, Math.round(size * 0.023));
  ctx.stroke();
  ctx.restore();

  // 3. Day number, centred
  ctx.save();
  ctx.fillStyle = resolvedTheme === "dark" ? "#f3f4f6" : "#1f2937";
  const fontSize = day >= 10 ? Math.round(size * 0.672) : Math.round(size * 0.75);
  ctx.font = `900 ${fontSize}px "DM Sans", "Inter", -apple-system, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.letterSpacing = `${Math.round(size * 0.047)}px`;
  ctx.fillText(String(day), Math.round(size / 2), Math.round(size * 0.58));
  ctx.restore();
}

function drawRoundRectFallback(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  if (r > w / 2) r = w / 2;
  if (r > h / 2) r = h / 2;
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
}

/* ── Favicon hook ──────────────────────────────────────────────────── */

let lastFaviconObjectUrl: string | null = null;

export function useDynamicFavicon() {
  const { resolved } = useTheme();
  const dayKey = useLocalDayKey();

  useEffect(() => {
    const draw = () => {
      if (typeof document === "undefined") return;
      const canvas = document.createElement("canvas");
      canvas.width = 256;
      canvas.height = 256;
      // willReadFrequently keeps this scratch canvas CPU-backed: reading pixels
      // back from a GPU canvas (toDataURL, and even toBlob's readback) blocks
      // the main thread until the WHOLE GPU raster queue flushes. This redraw
      // fires on theme-mode flips — the exact moment the entire page (and the
      // settings palette board canvas) repaints — where the stall measured
      // ~500ms. CPU canvas + async toBlob encode avoids both.
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) return;
      drawCalendarIcon(ctx, 256, resolved);
      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        updateFaviconLink(url);
        if (lastFaviconObjectUrl) URL.revokeObjectURL(lastFaviconObjectUrl);
        lastFaviconObjectUrl = url;
      }, "image/png");
    };

    draw();

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(draw);
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") draw();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    const intervalId = setInterval(draw, 10 * 60 * 1000);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearInterval(intervalId);
    };
  }, [resolved, dayKey]);
}

function updateFaviconLink(href: string) {
  if (typeof document === "undefined") return;
  const links = document.querySelectorAll("link[rel*='icon']");
  if (links.length === 0) {
    const newLink = document.createElement("link");
    newLink.rel = "icon";
    newLink.type = "image/png";
    newLink.href = href;
    document.head.appendChild(newLink);
  } else {
    links.forEach((link) => {
      (link as HTMLLinkElement).href = href;
    });
  }
}

/* ── Inline calendar icon component ────────────────────────────────── */

/** Renders the calendar-day icon as a visible canvas element.
 *  `size` is the CSS display size in pixels (canvas is drawn at 2× for retina). */
export function CalendarDayIcon({ size = 28, className }: { size?: number; className?: string }) {
  const { resolved } = useTheme();
  const dayKey = useLocalDayKey();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const scale = 2; // retina
    canvas.width = size * scale;
    canvas.height = size * scale;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    drawCalendarIcon(ctx, size * scale, resolved);

    // Redraw after fonts load
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        const c = canvasRef.current;
        if (!c) return;
        const cx = c.getContext("2d");
        if (cx) drawCalendarIcon(cx, size * scale, resolved);
      });
    }
  }, [resolved, size, dayKey]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{ width: size, height: size, display: "block", flexShrink: 0 }}
    />
  );
}
